import { useQuery } from "@apollo/client";
import { Add as AddIcon, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from "@mui/material";
import { ReactElement, useEffect, useRef, useState } from "react";

import {
  CreateResponseScriptGroupInput,
  CreateResponseScriptGroupMutation,
  CreateResponseScriptGroupMutationVariables,
  CreateResponseScriptInput,
  CreateResponseScriptMutation,
  CreateResponseScriptMutationVariables,
  GetSubaccountAdminFieldsQuery,
  GetSubaccountAdminFieldsQueryVariables,
  ImportExternalResponseScriptsMutation,
  ImportExternalResponseScriptsMutationVariables,
  ResponseScriptGroup,
  ResponseScriptImportJob,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import TooltipButton from "~frontend/components/TooltipButton";
import AddGroupDialog from "~frontend/components/responseScripts/AddGroupDialog";
import AddScriptDialog from "~frontend/components/responseScripts/AddScriptDialog";
import ImportScriptsDialog from "~frontend/components/responseScripts/ImportScriptsDialog";
import { ResponseScriptExternalImportSummary } from "~frontend/components/responseScripts/ResponseScriptExternalImportSummary";
import { ResponseScriptList } from "~frontend/components/responseScripts/ResponseScriptList";
import { GET_CAMPAIGN } from "~frontend/graphql/campaigns";
import {
  CREATE_RESPONSE_SCRIPT_GROUP,
  CREATE_RESPONSE_SCRIPT,
  LIST_RESPONSE_SCRIPT_GROUPS,
  IMPORT_EXTERNAL_RESPONSE_SCRIPTS,
} from "~frontend/graphql/scripts";
import { GET_SUBACCOUNT_ADMIN_FIELDS } from "~frontend/graphql/subaccounts";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { jobInProgress } from "~frontend/util/jobs";
import { userIsSubaccountAdminOrHigher } from "~frontend/util/roles";
import useMutation from "~frontend/util/useMutation";

import { StepInstructions } from "../StepInstructions";
import SaveButtons from "../campaignEditorUtils/SaveButtons";
import { CampaignForEditor } from "../types";
import { ScriptImporter } from "./ScriptImporter";

function responseScriptImportToCampaignJob(campaign: CampaignForEditor) {
  if (!campaign.responseScriptImportToCampaignJob) {
    return false;
  }

  return jobInProgress(campaign.responseScriptImportToCampaignJob);
}

export default function ResponseScripts({
  campaign,
  goToNextSection,
}: {
  campaign: CampaignForEditor;
  goToNextSection: (opts?: { bypassRouteLeavingGuard?: boolean }) => void;
}): ReactElement {
  const { subaccount, membership } = useSubaccount()!;
  const responseScriptImportsEnabled = useFeatureFlag("responseScriptImports");

  const { startPolling, stopPolling } = useQuery<
    GetSubaccountAdminFieldsQuery,
    GetSubaccountAdminFieldsQueryVariables
  >(GET_SUBACCOUNT_ADMIN_FIELDS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
    skip: !membership || !userIsSubaccountAdminOrHigher(membership),
  });

  const [
    globalResponseScriptsAccordionOpen,
    setGlobalResponesScriptsAccordionOpen,
  ] = useState(!campaign.responseScriptGroups.length);

  const [addGroupDialogOpen, toggleAddGroupDialog] = useState(false);

  const [addGroup] = useMutation<
    CreateResponseScriptGroupMutation,
    CreateResponseScriptGroupMutationVariables
  >(CREATE_RESPONSE_SCRIPT_GROUP, {
    onErrorSnackbarMessage: "Error adding group",
    refetchQueries: [LIST_RESPONSE_SCRIPT_GROUPS, GET_CAMPAIGN],
    awaitRefetchQueries: true,
  });

  const jobWasInProgress = useRef(responseScriptImportToCampaignJob(campaign));
  useEffect(() => {
    const currentStatus = responseScriptImportToCampaignJob(campaign);
    if (jobWasInProgress.current !== currentStatus) {
      if (currentStatus) {
        startPolling(1000);
      } else {
        stopPolling();
      }
    }
    jobWasInProgress.current = currentStatus;
  }, [campaign, jobWasInProgress, subaccount, startPolling, stopPolling]);

  async function handleAddGroup(
    values: Omit<CreateResponseScriptGroupInput, "subaccountId">
  ) {
    const { errors } = await addGroup({
      variables: {
        input: {
          title: values.title,
          includeInCampaignByDefault: values.includeInCampaignByDefault,
          subaccountId: subaccount.id,
          campaignId: campaign.id,
        },
      },
    });
    if (errors) {
      throw errors;
    } else {
      toggleAddGroupDialog(false);
    }
  }

  const [addScriptDialogOpen, toggleAddScriptDialog] = useState(false);

  const [addScript] = useMutation<
    CreateResponseScriptMutation,
    CreateResponseScriptMutationVariables
  >(CREATE_RESPONSE_SCRIPT, {
    refetchQueries: [LIST_RESPONSE_SCRIPT_GROUPS, GET_CAMPAIGN],
    awaitRefetchQueries: true,
  });

  async function handleAddScript(
    values: Omit<CreateResponseScriptInput, "subaccountId">
  ) {
    const { errors } = await addScript({
      variables: {
        input: {
          title: values.title,
          script: values.script,
          mediaUploadId: values.mediaUploadId,
          groupIds: values.groupIds,
          subaccountId: subaccount.id,
          tagIds: values.tagIds,
          keywordsToAdd: values.keywordsToAdd,
        },
      },
    });
    if (errors) {
      throw errors;
    } else {
      toggleAddScriptDialog(false);
    }
  }

  const [importScriptDialogOpen, toggleImportScriptsDialog] = useState(false);
  const [importScripts] = useMutation<
    ImportExternalResponseScriptsMutation,
    ImportExternalResponseScriptsMutationVariables
  >(IMPORT_EXTERNAL_RESPONSE_SCRIPTS, {
    refetchQueries: [GET_SUBACCOUNT_ADMIN_FIELDS, LIST_RESPONSE_SCRIPT_GROUPS],
    awaitRefetchQueries: true,
  });

  async function handleImportScripts(values: any) {
    const { errors } = await importScripts({
      variables: {
        input: {
          gsheetUrl: values.gsheetUrl,
          subaccountId: subaccount.id,
          campaignId: campaign.id,
        },
      },
    });
    if (errors) {
      throw errors;
    } else {
      toggleImportScriptsDialog(false);
    }
  }

  return (
    <>
      <Box sx={{ pb: 2 }}>
        <StepInstructions header="Response Scripts for Texters">
          Responses added here will appear in a searchable list for texters and
          in your campaign exports. You can import from your organization’s
          global response scripts or create new responses specific to this
          campaign. Any changes made to a response script on this page will only
          be reflected in this campaign, and will not change the global response
          script for your organization.
        </StepInstructions>
      </Box>
      <Accordion
        sx={{ backgroundColor: palette.lightestGray }}
        expanded={globalResponseScriptsAccordionOpen}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          onClick={() =>
            setGlobalResponesScriptsAccordionOpen(
              !globalResponseScriptsAccordionOpen
            )
          }
        >
          <Typography variant="body1Bold">Global Response Scripts</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ScriptImporter
            campaign={campaign}
            onImport={() => setGlobalResponesScriptsAccordionOpen(false)}
          />
        </AccordionDetails>
      </Accordion>
      {responseScriptImportsEnabled &&
        campaign.responseScriptImportToCampaignJob != null && (
          <Box sx={{ marginTop: "24px" }}>
            <ResponseScriptExternalImportSummary
              job={
                campaign.responseScriptImportToCampaignJob as ResponseScriptImportJob
              }
            />
          </Box>
        )}
      {campaign.responseScriptGroups.length > 0 && (
        <Card sx={{ marginTop: "24px" }}>
          <CardHeader title={`${campaign.name}'s Response Scripts`} />
          <CardContent sx={{ padding: 0 }}>
            <ResponseScriptList
              groups={campaign.responseScriptGroups as ResponseScriptGroup[]}
              campaign={campaign}
              sx={{ backgroundColor: "white" }}
            />
          </CardContent>
        </Card>
      )}
      <FlexBox justifyContent="space-between" sx={{ marginTop: "24px" }}>
        <FlexBox gap="10px">
          <TooltipButton
            startIcon={<AddIcon />}
            onClick={() => toggleAddScriptDialog(true)}
            disabled={!campaign.responseScriptGroups.length}
            tooltipText={
              !campaign.responseScriptGroups.length
                ? "Start by adding a group"
                : undefined
            }
            variant="contained"
            color="tertiary"
          >
            Add a Response
          </TooltipButton>
          <Button
            startIcon={<AddIcon />}
            onClick={() => toggleAddGroupDialog(true)}
            variant="contained"
            color="tertiary"
          >
            Add a Group
          </Button>
          {responseScriptImportsEnabled ? (
            <TooltipButton
              startIcon={<AddIcon />}
              key="import-response-scripts"
              id="import-response-scripts"
              onClick={() => toggleImportScriptsDialog(true)}
              tooltipText={"Import response scripts from a Google sheet"}
              disabled={
                campaign.responseScriptImportToCampaignJob?.status === "RUNNING"
              }
              variant="contained"
              color="tertiary"
            >
              Import Responses
            </TooltipButton>
          ) : null}
        </FlexBox>
        <SaveButtons
          variant="continueOnly"
          handleSave={() => {
            goToNextSection();
          }}
        />
      </FlexBox>
      <AddGroupDialog
        open={addGroupDialogOpen}
        onClose={() => toggleAddGroupDialog(false)}
        onSubmit={handleAddGroup}
        allowSettingSubaccountDefault={false}
      />
      <AddScriptDialog
        open={addScriptDialogOpen}
        scriptGroups={campaign.responseScriptGroups}
        onClose={() => toggleAddScriptDialog(false)}
        onSubmit={handleAddScript}
        campaign={campaign}
      />
      <ImportScriptsDialog
        open={importScriptDialogOpen}
        onClose={() => toggleImportScriptsDialog(false)}
        onSubmit={handleImportScripts}
      />
    </>
  );
}
